// The Escalations queue: every company customers have reported for missing
// financials, across every customer account.
//
// Filtering, sorting and pagination are all server-side (CMAPI /escalation ->
// RisikaAPI /global/financials/escalations), so the search box queries every
// page rather than only the rows currently loaded. Every filter is optional:
// with none selected the table shows the whole queue.

import React, { useEffect, useMemo, useState } from 'react'
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { FilterChip } from 'components/ui'
import { useEscalations } from 'services/queries/use-escalations'
import {
  EscalationListItem,
  EscalationStatusValue,
  EscalationUserRef,
} from 'types'
import {
  ALL_COUNTRIES_OPTION,
  CountryOption,
  buildCountryOptions,
  countryLabel,
  formatEscalationDate,
} from './escalations.helpers'
import RecordReasonDialog from './record-reason-dialog'

const statusColorMap: Record<string, 'warning' | 'success' | 'default'> = {
  OPEN: 'warning',
  REASON_RECORDED: 'success',
  CLOSED: 'default',
}

const STATUS_FILTERS: { label: string; value: EscalationStatusValue | '' }[] = [
  { label: 'All', value: '' },
  { label: 'Open', value: 'OPEN' },
  { label: 'Reason recorded', value: 'REASON_RECORDED' },
  { label: 'Closed', value: 'CLOSED' },
]

const headerCellSx = {
  py: 1.5,
  px: 2.5,
  bgcolor: 'action.hover',
  borderBottom: '1px solid',
  borderColor: 'divider',
}

const headerTypographySx = {
  fontSize: 12,
  fontWeight: 600,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.5px',
  color: 'text.secondary',
}

const bodyCellSx = {
  py: 1.75,
  px: 2.5,
  borderBottom: '1px solid',
  borderColor: 'divider',
}

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100]
const SEARCH_DEBOUNCE_MS = 400
const COLUMN_COUNT = 8

const CustomerCell: React.FC<{ escalation: EscalationListItem }> = ({
  escalation,
}) => {
  const customers = escalation.escalated_by
  if (customers.length === 0) {
    return <Typography sx={{ fontSize: 14 }}>—</Typography>
  }

  const [first, ...rest] = customers
  const label = first.name ?? `Customer #${first.company_id}`

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
      <Typography sx={{ fontSize: 14 }}>{label}</Typography>
      {rest.length > 0 && (
        <Tooltip
          title={rest
            .map(
              (customer) => customer.name ?? `Customer #${customer.company_id}`,
            )
            .join(', ')}
        >
          <Chip label={`+${rest.length}`} size="small" />
        </Tooltip>
      )}
    </Box>
  )
}

const UserCell: React.FC<{ users: EscalationUserRef[] }> = ({ users }) => {
  if (users.length === 0) {
    // Every escalation raised before RisikaAPI started recording the user.
    return (
      <Tooltip title="Raised before the escalating user was recorded">
        <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>
          —
        </Typography>
      </Tooltip>
    )
  }

  const [first, ...rest] = users

  return (
    <Box>
      <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
        {first.name ?? 'Unknown user'}
      </Typography>
      <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
        {first.email ?? `User #${first.api_user_id}`}
      </Typography>
      {rest.length > 0 && (
        <Tooltip
          title={rest
            .map((user) => user.email ?? `User #${user.api_user_id}`)
            .join(', ')}
        >
          <Chip label={`+${rest.length}`} size="small" sx={{ mt: 0.5 }} />
        </Tooltip>
      )}
    </Box>
  )
}

const EscalationsTable: React.FC = () => {
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [country, setCountry] = useState<CountryOption>(ALL_COUNTRIES_OPTION)
  const [status, setStatus] = useState<EscalationStatusValue | ''>('')
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [editing, setEditing] = useState<EscalationListItem | null>(null)

  // Debounce the search box so we don't hit the API on every keystroke; a new
  // search always starts from page 1.
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim())
      setPage(1)
    }, SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [searchInput])

  const { data: response, isLoading } = useEscalations({
    page,
    pageSize,
    countries: country.code ? [country.code] : undefined,
    status: status ? [status] : undefined,
    q: search || undefined,
  })

  const payload = response && response.success ? response.data : null

  // Built from the countries that actually have escalations, so the dropdown
  // can never offer one with no rows — nor list the same country twice, which
  // is what merging the mixed-case zone country sets used to do.
  const countryOptions = useMemo(
    () => buildCountryOptions(payload?.available_countries ?? []),
    [payload],
  )

  const escalations = payload?.escalations ?? []
  const pagination = payload?.pagination ?? {
    page,
    page_size: pageSize,
    total: 0,
    pages: 1,
  }

  const hasFilters = Boolean(country.code || status || search)

  const clearFilters = () => {
    setCountry(ALL_COUNTRIES_OPTION)
    setStatus('')
    setSearchInput('')
    setSearch('')
    setPage(1)
  }

  if (!isLoading && !payload) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography sx={{ fontSize: 15, fontWeight: 600, mb: 0.5 }}>
          Could not load escalations
        </Typography>
        <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
          Try reloading the page. If the problem persists, check that
          CustomerManagementAPI can reach RisikaAPI.
        </Typography>
      </Box>
    )
  }

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          px: 3,
          py: 2.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: 'wrap',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box>
          <Typography
            sx={{ fontSize: 16, fontWeight: 600, color: 'text.primary' }}
          >
            Escalations
          </Typography>
          <Typography sx={{ fontSize: 13, color: 'text.secondary', mt: 0.25 }}>
            Companies reported for missing financials, across all customers
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            flexWrap: 'wrap',
          }}
        >
          <Autocomplete
            value={country}
            onChange={(_event, newValue) => {
              setCountry(newValue ?? ALL_COUNTRIES_OPTION)
              setPage(1)
            }}
            options={countryOptions}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) => option.code === value.code}
            // The filter always has a value ("All countries" when cleared), so
            // it can never fall back to null and appear to reset itself.
            disableClearable
            renderInput={(params) => <TextField {...params} label="Country" />}
            sx={{ width: 260 }}
            size="small"
            data-cy="escalations-country-filter"
          />
          <TextField
            label="Search"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            placeholder="Local ID…"
            size="small"
            sx={{ width: 200, '& .MuiOutlinedInput-root': { fontSize: 13 } }}
            data-cy="escalations-search"
          />
          {hasFilters && (
            <Button onClick={clearFilters} data-cy="escalations-clear-filters">
              Clear filters
            </Button>
          )}
        </Box>
      </Box>

      <Box
        sx={{
          px: 3,
          py: 1.5,
          display: 'flex',
          gap: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        {STATUS_FILTERS.map((filter) => (
          <FilterChip
            key={filter.value || 'all'}
            label={filter.label}
            active={status === filter.value}
            onClick={() => {
              setStatus(filter.value)
              setPage(1)
            }}
            data-cy={`escalations-status-${filter.value || 'all'}`}
          />
        ))}
      </Box>

      <TableContainer>
        <Table sx={{ minWidth: 900 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={headerCellSx}>
                <Typography sx={headerTypographySx}>Company</Typography>
              </TableCell>
              <TableCell sx={{ ...headerCellSx, width: 160 }}>
                <Typography sx={headerTypographySx}>Country</Typography>
              </TableCell>
              <TableCell sx={{ ...headerCellSx, width: 150 }}>
                <Typography sx={headerTypographySx}>Status</Typography>
              </TableCell>
              <TableCell sx={headerCellSx}>
                <Typography sx={headerTypographySx}>Escalated by</Typography>
              </TableCell>
              <TableCell sx={headerCellSx}>
                <Typography sx={headerTypographySx}>Raised by</Typography>
              </TableCell>
              <TableCell sx={{ ...headerCellSx, width: 90 }} align="right">
                <Typography sx={headerTypographySx}>Count</Typography>
              </TableCell>
              <TableCell sx={{ ...headerCellSx, width: 190 }}>
                <Typography sx={headerTypographySx}>Last escalated</Typography>
              </TableCell>
              <TableCell sx={{ ...headerCellSx, width: 260 }}>
                <Typography sx={headerTypographySx}>Reason</Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell
                  colSpan={COLUMN_COUNT}
                  sx={{ py: 8, textAlign: 'center' }}
                >
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            )}

            {!isLoading && escalations.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={COLUMN_COUNT}
                  sx={{ py: 8, textAlign: 'center' }}
                >
                  <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>
                    {hasFilters
                      ? 'No escalations match these filters'
                      : 'No escalations have been raised yet'}
                  </Typography>
                  {hasFilters && (
                    <Button onClick={clearFilters} sx={{ mt: 1 }}>
                      Clear filters
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            )}

            {!isLoading &&
              escalations.map((escalation) => (
                <TableRow
                  key={escalation.id}
                  hover
                  data-cy={`escalation-row-${escalation.local_id}`}
                >
                  <TableCell sx={bodyCellSx}>
                    <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                      {escalation.company_name ?? escalation.local_id}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                      {escalation.company_name
                        ? escalation.local_id
                        : 'Name unavailable'}
                    </Typography>
                  </TableCell>
                  <TableCell sx={bodyCellSx}>
                    <Typography sx={{ fontSize: 14 }}>
                      {countryLabel(escalation.country)}
                    </Typography>
                  </TableCell>
                  <TableCell sx={bodyCellSx}>
                    <Chip
                      label={escalation.status}
                      color={statusColorMap[escalation.status] || 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell sx={bodyCellSx}>
                    <CustomerCell escalation={escalation} />
                  </TableCell>
                  <TableCell sx={bodyCellSx}>
                    <UserCell users={escalation.escalated_by_users} />
                  </TableCell>
                  <TableCell sx={bodyCellSx} align="right">
                    <Typography sx={{ fontSize: 14 }}>
                      {escalation.escalation_count}
                    </Typography>
                  </TableCell>
                  <TableCell sx={bodyCellSx}>
                    <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>
                      {formatEscalationDate(escalation.last_escalated_at)}
                    </Typography>
                  </TableCell>
                  <TableCell sx={bodyCellSx}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        justifyContent: 'space-between',
                      }}
                    >
                      <Tooltip title={escalation.reason ?? ''}>
                        <Typography noWrap sx={{ fontSize: 14, maxWidth: 150 }}>
                          {escalation.reason ?? '—'}
                        </Typography>
                      </Tooltip>
                      <Button
                        size="small"
                        onClick={() => setEditing(escalation)}
                        data-cy={`escalation-record-${escalation.local_id}`}
                      >
                        {escalation.reason ? 'Edit' : 'Record'}
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={pagination.total}
        page={page - 1}
        onPageChange={(_event, newPage) => setPage(newPage + 1)}
        rowsPerPage={pageSize}
        onRowsPerPageChange={(event) => {
          setPageSize(parseInt(event.target.value, 10))
          setPage(1)
        }}
        rowsPerPageOptions={PAGE_SIZE_OPTIONS}
        data-cy="escalations-pagination"
        // Left-align the controls (MUI right-aligns them via a flex spacer) so
        // they stay clear of the HubSpot chat bubble fixed to the viewport's
        // bottom-right corner
        sx={{ '& .MuiTablePagination-spacer': { display: 'none' } }}
      />

      <RecordReasonDialog
        escalation={editing}
        onClose={() => setEditing(null)}
      />
    </Paper>
  )
}

export default EscalationsTable
